<script setup lang="ts">
import EnglishLessonPage from "~/features/english/components/EnglishLessonPage.vue";

const textbookAlignedPoints = [
  "Unit clean water ko survival, health aur sustainable development ki basic need ke tor par present karta hai.",
  "Chapter me water scarcity, pollution, climate patterns aur human activity ko clean water resources ke liye major threats bataya gaya hai.",
  "Is lesson ka exam focus problem, causes, effects aur solutions ko clear structure me explain karna hota hai.",
];

const chapterCoverage = [
  "Importance of clean water",
  "Threats to water resources",
  "Pollution and public health",
  "Climate change and water scarcity",
  "Human misuse and overuse",
  "Water-borne diseases",
  "Conservation and protection steps",
];

const quickRevisionPoints = [
  "Clean water peene, hygiene, agriculture aur health ke liye basic need hai.",
  "Industrial waste, sewage aur chemicals water quality ko damage karte hain.",
  "Unsafe water diarrhea, cholera aur stomach diseases ka sabab ban sakta hai.",
  "Climate patterns aur drought conditions water availability ko affect karte hain.",
  "Responsible use, treatment plants aur awareness key solutions hain.",
  "Lesson ka message individual aur collective responsibility dono par based hai.",
];

const readingQuestions = [
  {
    question: "Why is clean water essential for human life?",
    answer: "Clean water peene, cooking, hygiene, agriculture aur overall health ke liye zaroori hai. Is ke baghair diseases barh jati hain aur normal life aur development dono affect hote hain.",
  },
  {
    question: "What are the major threats to clean water resources?",
    answer: "Major threats me industrial waste, sewage, agricultural chemicals, careless human use, aur changing climate patterns shamil hain. Yeh factors mil kar water quality aur availability dono ko damage karte hain.",
  },
  {
    question: "How does polluted or unsafe water affect public health?",
    answer: "Unsafe water se diarrhea, cholera, stomach infections aur doosri water-borne diseases phailti hain. Is ka sab se zyada asar children aur low-resource communities par hota hai.",
  },
  {
    question: "How do climate and human activity threaten water resources?",
    answer: "Climate change rainfall patterns ko disturb karti hai aur droughts ya shortages create kar sakti hai, jab ke human activity pollution aur overuse ke through situation ko aur serious bana deti hai.",
  },
  {
    question: "What steps can society take to protect clean water?",
    answer: "Water conservation, pollution control, proper waste disposal, treatment plants, awareness campaigns aur responsible use clean water protect karne ke major steps hain.",
  },
  {
    question: "What is the central message of the lesson Clean Water?",
    answer: "Central message yeh hai ke clean water ko taken for granted nahi lena chahiye. Is ko protect karna individual aur collective responsibility dono hai, warna health aur future development dono risk me par jate hain.",
  },
];

const mcqs = [
  {
    question: "Choose the option that is the best synonym in the context of the text: we rely on clean water to survive, but patterns of climate and human activity are threatening our clean resources of water.",
    options: [
      { label: "a", text: "Protecting", verdict: "incorrect", reason: "Sentence ka meaning opposite ho jata hai. Text danger ki baat kar raha hai, safety ki nahi." },
      { label: "b", text: "Endangering", verdict: "correct", reason: "Threatening ka closest contextual synonym endangering hai, kyun ke dono nuksan aur risk ka sense dete hain." },
      { label: "c", text: "Enhancing", verdict: "incorrect", reason: "Enhancing improve karne ka sense deta hai, jab ke chapter clean water par negative impact discuss karta hai." },
      { label: "d", text: "Preserving", verdict: "incorrect", reason: "Preserving protect aur save karne ke liye use hota hai, jo threatening ke bilkul ulat hai." },
    ],
  },
  {
    question: "Which factor is identified as a major threat to clean water resources?",
    options: [
      { label: "a", text: "proper waste treatment", verdict: "incorrect", reason: "Proper treatment threat nahi, solution hai." },
      { label: "b", text: "industrial waste and sewage", verdict: "correct", reason: "Chapter pollution sources me industrial waste aur sewage ko major threat batata hai." },
      { label: "c", text: "responsible household use", verdict: "incorrect", reason: "Responsible use water ko protect karta hai, damage nahi." },
      { label: "d", text: "public awareness campaigns", verdict: "incorrect", reason: "Awareness campaigns preventive measure hain, threat nahi." },
    ],
  },
  {
    question: "What is the best collective response suggested by the lesson?",
    options: [
      { label: "a", text: "ignore the issue until shortages become severe", verdict: "incorrect", reason: "Lesson prevention aur timely action ko support karta hai." },
      { label: "b", text: "leave water protection only to one department", verdict: "incorrect", reason: "Text individual aur collective responsibility dono ki baat karta hai." },
      { label: "c", text: "conservation, pollution control, and awareness", verdict: "correct", reason: "Yehi combined approach chapter ke solution structure se match karti hai." },
      { label: "d", text: "focus only on bottled water", verdict: "incorrect", reason: "Chapter system-level water safety aur protection discuss karta hai, sirf private purchase nahi." },
    ],
  },
];

const answerBuilding = [
  "Importance aur health impact ko alag headings me likhne se answer zyada clear hota hai.",
  "Causes wale jawab me climate aur human activity dono mention karo, sirf pollution tak answer limit na karo.",
  "Solutions me conservation, awareness aur proper management ka combination dena behtar hota hai.",
  "Public health wale answer me 1-2 water-borne diseases ka zikr zaroor karo.",
  "Agar central message poocha jaye to `basic need + shared responsibility` ka structure use karo.",
];

usePageSeo({
  title: "Clean Water Notes | 11th Class English Unit 8",
  description: "11th class English Unit 8 Clean Water notes with reading answers, public health points, water pollution, conservation and explained MCQs.",
  keywords: "clean water notes, 11th class english unit 8, class 11 english chapter 8, clean water short questions, clean water mcqs",
  breadcrumbs: [
    { name: "Home", item: "/" },
    { name: "11th English", item: "/11th/english/" },
    { name: "Prose", item: "/11th/english/prose/" },
    { name: "Clean Water", item: "/11th/english/prose/clean-water/" },
  ],
});
</script>

<template>
  <EnglishLessonPage
    unit="Unit 8"
    title="Clean Water"
    intro="Yeh page chapter ke environmental aur public-health focus ko exam prep format me organize karta hai. Is unit me problem-solution structure sab se important hota hai."
    :bullet-sections="[
      { title: 'Textbook-Aligned Snapshot', items: textbookAlignedPoints },
      { title: 'Unit 8 Coverage Checklist', items: chapterCoverage, layout: 'grid' },
      { title: 'Quick Revision Points', items: quickRevisionPoints },
      { title: 'Answer-Building Notes', items: answerBuilding }
    ]"
    :text-sections="[
      { title: 'Reading and Critical Thinking Answers', items: readingQuestions.map((item) => ({ title: item.question, description: item.answer })) }
    ]"
    :mcq-sections="[
      { title: 'MCQs with Option Explanation', items: mcqs }
    ]"
  />
</template>





